import React, { useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import {
  calculateCableQuantities,
  calculateTerminalQuantities,
  calculateSiglatureAndMorsetti,
  calculateSheathQuantities
} from '../../utils/quantities';
import { renderTerminalIconSimple } from '../../utils/terminalRenderLogic';
import { getTerminalData, hasCustomTerminalGraphic, convertMetricToAWG } from '../../utils/cableLogic';
import { exportSiglature } from '../../utils/export';
import { translateColorForDisplay } from '../../utils/colorUtils';
import { Icons } from '../icons/Icons';
import CableColorRect from '../ui/CableColorRect';

const SummaryBadge = ({ children, tone = 'neutral' }) => {
  const classes = {
    neutral: 'bg-gray-200 text-gray-700',
    accent: 'bg-orange-500 text-white shadow-md',
    purple: 'bg-purple-500 text-white shadow-md'
  };

  return (
    <div className={`px-3 py-1 rounded-full text-sm font-bold ${classes[tone]}`}>
      {children}
    </div>
  );
};

const SectionCard = ({ title, icon, accentClass, badge, children }) => (
  <section className={`${accentClass} rounded-lg p-6`}>
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
      <h3 className="text-lg font-semibold flex items-center gap-3">
        {icon}
        {title}
      </h3>
      {badge}
    </div>
    {children}
  </section>
);

const QuantitiesForm = ({ cables, settings, terminali = {}, availableSheaths = [], setWarnings, setSuccessMessage }) => {
  const { t, language } = useI18n();

  const quantities = useMemo(() => calculateCableQuantities(cables, settings), [cables, settings]);
  const terminalQuantities = useMemo(() => calculateTerminalQuantities(cables, settings, terminali), [cables, settings, terminali]);
  const sheathQuantities = useMemo(() => calculateSheathQuantities(cables, availableSheaths), [cables, availableSheaths]);

  const siglaturaData = useMemo(() => {
    if (cables.length === 0) return null;

    const allResults = calculateSiglatureAndMorsetti(cables, settings);
    const total25_6 = allResults.section25_6.reduce((sum, item) => sum + item.quantita, 0);
    const total05_15 = allResults.section05_15.reduce((sum, item) => sum + item.quantita, 0);
    const totalAbove6 = allResults.sectionAbove6.reduce((sum, item) => sum + item.quantita, 0);
    const totalMorsetti = allResults.morsetti.reduce((sum, item) => sum + item.quantita, 0);

    return {
      section25_6: allResults.section25_6,
      section05_15: allResults.section05_15,
      sectionAbove6: allResults.sectionAbove6,
      morsetti: allResults.morsetti,
      total25_6,
      total05_15,
      totalAbove6,
      totalMorsetti,
      totalOverall: total25_6 + total05_15 + totalAbove6 + totalMorsetti
    };
  }, [cables, settings]);

  const totalCables = cables.length;
  const totalLength = cables.reduce((sum, cable) => sum + (cable.lunghezza || 0), 0);
  const realTotalCables = totalCables * settings.quantitaCablaggi;
  const realTotalLength = totalLength * settings.quantitaCablaggi;
  const totalTerminals = terminalQuantities.reduce((sum, item) => sum + item.count, 0);
  const realTotalTerminals = totalTerminals * settings.quantitaCablaggi;
  const totalSheathLength = sheathQuantities.reduce((sum, item) => sum + item.totalLength, 0);
  const totalSheathCount = sheathQuantities.reduce((sum, item) => sum + item.count, 0);
  const hasTerminals = totalTerminals > 0;
  const hasSheaths = sheathQuantities.length > 0;
  const hasSiglature = siglaturaData && siglaturaData.totalOverall > 0;

  const productionBadge = settings.quantitaCablaggi > 1
    ? `×${settings.quantitaCablaggi} ${t('quantities.cablaggi')}`
    : '×1';

  const productionNotice = settings.quantitaCablaggi > 1 ? (
    <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 mb-4">
      <div className="flex items-center gap-2">
        <span className="text-orange-600 text-xl">⚠</span>
        <div>
          <div className="text-sm font-bold text-orange-800">
            Produzione Multiple: {settings.quantitaCablaggi} cablaggi identici
          </div>
          <div className="text-xs text-orange-700 mt-1">
            I totali reali sono moltiplicati per {settings.quantitaCablaggi}. Verifica le quantità prima dell'ordine materiali.
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const siglatureSections = hasSiglature
    ? [
        siglaturaData.total05_15 > 0 && {
          key: 'section05_15',
          title: t('quantities.signatures05_15'),
          colorClass: 'text-green-700',
          footerClass: 'border-green-500 bg-green-100',
          badgeClass: 'bg-green-500 text-white',
          data: siglaturaData.section05_15
        },
        siglaturaData.total25_6 > 0 && {
          key: 'section25_6',
          title: t('quantities.signatures25_6'),
          colorClass: 'text-blue-700',
          footerClass: 'border-blue-500 bg-blue-100',
          badgeClass: 'bg-blue-500 text-white',
          data: siglaturaData.section25_6
        },
        siglaturaData.totalAbove6 > 0 && {
          key: 'sectionAbove6',
          title: t('quantities.flatTags'),
          colorClass: 'text-red-700',
          footerClass: 'border-red-500 bg-red-100',
          badgeClass: 'bg-red-500 text-white',
          data: siglaturaData.sectionAbove6
        },
        siglaturaData.totalMorsetti > 0 && {
          key: 'morsetti',
          title: t('quantities.terminals'),
          colorClass: 'text-orange-700',
          footerClass: 'border-orange-500 bg-orange-100',
          badgeClass: 'bg-orange-500 text-white',
          data: siglaturaData.morsetti
        }
      ].filter(Boolean)
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">{t('quantities.summaryTitle')}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'it' ? 'Riepiloghi sempre visibili, mostrati solo quando ci sono dati utili.' : 'Summaries stay visible and appear only when useful data exists.'}
          </p>
        </div>
        <SummaryBadge tone={settings.quantitaCablaggi > 1 ? 'accent' : 'neutral'}>
          {productionBadge}
        </SummaryBadge>
      </div>

      <SectionCard
        title={t('quantities.cableQuantities')}
        icon={<Icons.FileSpreadsheet className="w-5 h-5 text-green-700" />}
        accentClass="bg-green-50"
        badge={<SummaryBadge tone={settings.quantitaCablaggi > 1 ? 'accent' : 'neutral'}>{productionBadge}</SummaryBadge>}
      >
        {productionNotice}
        <div className="bg-white rounded-md overflow-hidden border border-green-100">
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b z-10">
                <tr>
                  <th className="text-left py-2 pl-4">{t('quantities.cableType')}</th>
                  <th className="text-center py-2">{t('quantities.quantity')}</th>
                  <th className="text-center py-2">{t('quantities.totalLength')}</th>
                  {settings.quantitaCablaggi > 1 ? (
                    <>
                      <th className="text-center py-2 text-orange-700">{t('quantities.realTotal')}</th>
                      <th className="text-center py-2 text-orange-700 pr-4">{t('quantities.realLength')}</th>
                    </>
                  ) : (
                    <th className="pr-4"></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {quantities.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3 pl-4 flex items-center gap-3">
                      {item.isMultipolar ? (
                        <>
                          <div className="w-5 h-5 bg-purple-500 rounded flex items-center justify-center text-white text-xs font-bold">M</div>
                          <span>
                            {item.codiceInterno || item.tipo || (
                              <>
                                {translateColorForDisplay(item.colore, language)}
                                {item.poleColors?.length > 0 && ` · ${item.poleColors.map((color) => translateColorForDisplay(color, language)).join(', ')}`}
                              </>
                            )}
                            {item.descrizione && <span className="block text-xs text-gray-600" title={item.descrizione}>{item.descrizione}</span>}
                            {item.codiceInterno && (
                              <span className="block text-xs text-gray-500">
                                {translateColorForDisplay(item.colore, language)}
                                {item.poleColors?.length > 0 && ` · ${item.poleColors.map((color) => translateColorForDisplay(color, language)).join(', ')}`}
                              </span>
                            )}
                          </span>
                        </>
                      ) : (
                        <>
                          <CableColorRect
                            color={item.colore}
                            width="20px"
                            height="20px"
                            borderRadius="4px"
                            border="1px solid #ddd"
                          />
                          <span>
                            {item.codiceInterno || (settings?.useAWG ? `AWG${convertMetricToAWG(item.sezione)} · ${translateColorForDisplay(item.colore, language)}` : `${item.sezione}mm² · ${translateColorForDisplay(item.colore, language)}`)}
                            {item.codiceInterno && (
                              <span className="block text-xs text-gray-500">
                                {item.descrizione && <span className="block text-gray-600" title={item.descrizione}>{item.descrizione}</span>}
                                {settings?.useAWG ? `AWG${convertMetricToAWG(item.sezione)} · ${translateColorForDisplay(item.colore, language)}` : `${item.sezione}mm² · ${translateColorForDisplay(item.colore, language)}`}
                              </span>
                            )}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="text-center py-3">{item.count}</td>
                    <td className="text-center py-3">{(item.totalLength / 1000).toFixed(2)}m</td>
                    {settings.quantitaCablaggi > 1 ? (
                      <>
                        <td className="text-center py-3 text-orange-700 font-bold">
                          {item.count * settings.quantitaCablaggi}
                        </td>
                        <td className="text-center py-3 text-orange-700 font-bold pr-4">
                          {((item.totalLength * settings.quantitaCablaggi) / 1000).toFixed(2)}m
                        </td>
                      </>
                    ) : (
                      <td className="pr-4"></td>
                    )}
                  </tr>
                ))}
                <tr className="border-t-2 border-green-500 bg-green-100 font-bold sticky bottom-0">
                  <td className="py-4 pl-4 text-green-800">
                    {t('quantities.total')} ({quantities.length} {t('quantities.varieties')})
                  </td>
                  <td className="text-center py-4 text-green-800">{totalCables}</td>
                  <td className="text-center py-4 text-green-800">{(totalLength / 1000).toFixed(2)}m</td>
                  {settings.quantitaCablaggi > 1 ? (
                    <>
                      <td className="text-center py-4 text-orange-700 font-bold">{realTotalCables}</td>
                      <td className="text-center py-4 text-orange-700 font-bold pr-4">{(realTotalLength / 1000).toFixed(2)}m</td>
                    </>
                  ) : (
                    <td className="pr-4"></td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </SectionCard>

      {hasTerminals && (
        <SectionCard
          title={t('quantities.terminalQuantities')}
          icon={<Icons.Wire className="w-5 h-5 text-indigo-700" />}
          accentClass="bg-indigo-50"
          badge={<SummaryBadge tone={settings.quantitaCablaggi > 1 ? 'accent' : 'neutral'}>{productionBadge}</SummaryBadge>}
        >
          {productionNotice}
          <div className="bg-white rounded-md overflow-hidden border border-indigo-100">
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b z-10">
                  <tr>
                    <th className="text-left py-2 pl-4">{t('quantities.terminalType')}</th>
                    <th className="text-center py-2">{t('quantities.quantity')}</th>
                    {settings.quantitaCablaggi > 1 ? (
                      <th className="text-center py-2 text-orange-700 pr-4">{t('quantities.realTotal')}</th>
                    ) : (
                      <th className="pr-4"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {terminalQuantities.map((item, index) => {
                    const tData = getTerminalData(item.terminal, { ...settings, lingua: language }, terminali);
                    const hasCustomGraphic = hasCustomTerminalGraphic(item.terminal);

                    return (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 pl-4 flex items-center gap-4">
                          <div className="flex items-center justify-center flex-shrink-0">
                            {hasCustomGraphic ? (
                              renderTerminalIconSimple(item.terminal, 56, 28, terminali)
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-indigo-500 border-2 border-indigo-600 flex items-center justify-center">
                                <span className="text-white text-sm font-bold">
                                  {item.terminal.charAt(0)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900">{item.terminal}</div>
                            <div className="text-xs text-gray-600 truncate">
                              {tData?.nome || t('quantities.terminal')}
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-3 font-semibold">{item.count}</td>
                        {settings.quantitaCablaggi > 1 ? (
                          <td className="text-center py-3 text-orange-700 font-bold pr-4">
                            {item.count * settings.quantitaCablaggi}
                          </td>
                        ) : (
                          <td className="pr-4"></td>
                        )}
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-indigo-500 bg-indigo-100 font-bold sticky bottom-0">
                    <td className="py-4 pl-4 text-indigo-800">
                      {t('quantities.total')} ({terminalQuantities.length} {t('quantities.varieties')})
                    </td>
                    <td className="text-center py-4 text-indigo-800">{totalTerminals}</td>
                    {settings.quantitaCablaggi > 1 ? (
                      <td className="text-center py-4 text-orange-700 font-bold pr-4">{realTotalTerminals}</td>
                    ) : (
                      <td className="pr-4"></td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </SectionCard>
      )}

      {hasSheaths && (
        <SectionCard
          title={t('quantities.sheathQuantities', 'Riepilogo Guaine')}
          icon={<span className="text-xl text-orange-700">🛡</span>}
          accentClass="bg-orange-50"
          badge={<SummaryBadge tone={settings.quantitaCablaggi > 1 ? 'accent' : 'neutral'}>{productionBadge}</SummaryBadge>}
        >
          {productionNotice}
          <div className="bg-white rounded-md overflow-hidden border border-orange-100">
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b z-10">
                  <tr>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">{t('quantities.sheathType', 'Tipo')}</th>
                    <th className="text-center py-3 font-bold text-gray-700">{t('quantities.diameter', 'Diametro')}</th>
                    <th className="text-center py-3 font-bold text-gray-700">{t('quantities.totalLength', 'Lungh. Totale')}</th>
                    <th className="text-center py-3 font-bold text-gray-700">{t('quantities.quantity', 'Quantità')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sheathQuantities.map((item, index) => {
                    const displayType = item.type === 'PVC'
                      ? t('quantities.sheathPVC')
                      : item.type === 'Braided'
                        ? t('quantities.sheathBraided')
                        : item.type;

                    return (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="py-3 px-4 font-medium text-gray-900">{displayType}</td>
                        <td className="text-center py-3">Ø {item.diameter}</td>
                        <td className="text-center py-3">{item.totalLength} mm</td>
                        <td className="text-center py-3 font-bold text-orange-600">{item.count}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0 bg-orange-100 font-bold border-t-2 border-orange-200">
                  <tr>
                    <td className="py-3 pl-4 text-orange-900">{t('quantities.total', 'TOTALE')}</td>
                    <td className="text-center py-3"></td>
                    <td className="text-center py-3 text-orange-900">{(totalSheathLength / 1000).toFixed(2)}m</td>
                    <td className="text-center py-3 text-orange-900 text-lg">{totalSheathCount}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </SectionCard>
      )}

      {hasSiglature && (
        <SectionCard
          title={t('quantities.signatureCalculation')}
          icon={<Icons.FileSpreadsheet className="w-5 h-5 text-purple-700" />}
          accentClass="bg-purple-50"
          badge={<SummaryBadge tone="purple">{siglaturaData.totalOverall} {t('quantities.totalLabel')}</SummaryBadge>}
        >
          <div className="bg-white rounded-md p-4 mb-4 border border-purple-100">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-3">
              <h4 className="font-bold text-purple-800">{t('quantities.summaryTotals')}</h4>
              <button
                onClick={() => exportSiglature(cables, { ...settings, lingua: language }, setWarnings, setSuccessMessage)}
                disabled={!siglaturaData}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-gray-300 transition-colors flex items-center gap-2"
              >
                <Icons.Download className="w-4 h-4" />
                {t('quantities.exportSignatures')}
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">{siglaturaData.total05_15}</div>
                <div className="text-sm text-gray-600">{t('quantities.signatures05_15')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{siglaturaData.total25_6}</div>
                <div className="text-sm text-gray-600">{t('quantities.signatures25_6')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{siglaturaData.totalAbove6}</div>
                <div className="text-sm text-gray-600">{t('quantities.flatTags')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">{siglaturaData.totalMorsetti}</div>
                <div className="text-sm text-gray-600">{t('quantities.terminals')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{siglaturaData.totalOverall}</div>
                <div className="text-sm text-gray-600">{t('quantities.totalOverall')}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {siglatureSections.map((section) => (
              <div key={section.key} className="bg-white rounded-md overflow-hidden border border-gray-100">
                <h5 className={`font-bold p-4 pb-3 ${section.colorClass}`}>
                  {section.title}
                </h5>
                <div className="h-80 overflow-y-auto">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[30%]" />
                      <col className="w-[10%]" />
                      <col className="w-[30%]" />
                      <col className="w-[30%]" />
                    </colgroup>
                    <thead className="sticky top-0 bg-gray-50 border-b z-10">
                      <tr>
                        <th className="text-center py-2 font-medium text-gray-700">{t('quantities.start')}</th>
                        <th className="text-center py-2 font-medium text-gray-700">{t('quantities.repeat')}</th>
                        <th className="text-center py-2 font-medium text-gray-700">{t('quantities.end')}</th>
                        <th className="text-center py-2 font-medium text-gray-700 border-l border-gray-200">{t('quantities.quantity')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.data.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="text-center py-3 font-medium truncate" title={item.inizio}>{item.inizio}</td>
                          <td className="text-center py-3">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-bold text-xs">
                              {item.ripetizioni}
                            </span>
                          </td>
                          <td className="text-center py-3 font-medium">{item.fine || '—'}</td>
                          <td className="text-center py-3 border-l border-gray-200">
                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-bold text-xs">
                              {item.quantita}
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className={`border-t-2 font-bold sticky bottom-0 ${section.footerClass}`}>
                        <td className="py-4 pl-8 text-left text-gray-800">{t('quantities.total')}</td>
                        <td className="py-4"></td>
                        <td className="py-4"></td>
                        <td className="text-center py-4 border-l border-gray-200">
                          <span className={`px-2 py-1 rounded font-bold text-xs ${section.badgeClass}`}>
                            {section.data.reduce((sum, item) => sum + item.quantita, 0)}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default QuantitiesForm;
